import AddCircle from "@mui/icons-material/AddCircle";
import CommentIcon from "@mui/icons-material/Comment";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RemoveCircleOutline from "@mui/icons-material/RemoveCircleOutline";
import { Checkbox, IconButton, Stack, TextField, Tooltip, Typography } from "@mui/material";
import {
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  gridDetailPanelExpandedRowsContentCacheSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid-pro";
import { DateTime } from "luxon";
import React from "react";
import BidLineDayCell from "../../components/bid/BidLineDayCell.jsx";
import { formatDaysOffKey } from "../../util/bid/bidFormUtils";
import { getTimeZoneFromSwaStation } from "../../util/timeZoneService.js";
import { csvToJson } from "../commonUtils.js";
import {
  IAM_CONTRACT,
  NON_CONTRACT,
  SENIORITY_MISSING_DATA,
  TWU_CONTRACT,
  WEEKDAYS_SHORT,
  WORKGROUP_MANAGER,
} from "../constants";
import { ALL_BID_DAYS_OFF_FILTERS } from "./bidConstants";
import { isDateTimeInPast } from "../dateHelper";
import { sortSeniorityList } from "../filterSeniorityList";
import { BID_GROUP_ADD_EMPLOYEE_WARNING_MESSAGE } from "../messageConstants";
import { isDeletedBid, isPendingBid, isAwardedBid } from "./bidCommon.js";
import { validateBidLines } from "./bidInputValidation";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { getPermanentBidPositionsByContract } from "../../api/commonConfig/bidConfig";
import { getPresignedUrl, uploadCSVFileToS3 } from "../../api/system.js";
import { getEmployeeUnrankedCount, getEligibleEmployees } from "./bidGroupUtils";
import BidAwardNameCell from "../../components/bid/BidAwardNameCell.jsx";
import { getShiftDate } from "../dateTimeFormatter";
import { getUnionContractGivenWorkgroup } from "../workgroupMapper.js";

export const sendBidLineImportFileToS3 = async (station, referenceNumber, fileName, fileData) => {
  try {
    const timeZone = await getTimeZoneFromSwaStation(station);
    const s3ObjectName = encodeURIComponent(
      `BID_${referenceNumber}/bidLineImportFiles/${DateTime.now().setZone(timeZone).toISO()}_${fileName}`
    );
    getPresignedUrl("put_object", "bid-data", s3ObjectName, "text/csv")
      .then((res) => res.json())
      .then((result) => {
        if (result.presignedUrl) {
          uploadCSVFileToS3(result.presignedUrl, fileData);
        }
      })
      .catch((err) => {
        console.error("Error: ", err);
      });
  } catch (e) {
    console.error("Error: ", e);
  }
};

export const validateAndTransformBidFileToJson = async (fileContents, contract) => {
  let result = { bidLines: [] };

  const lines = fileContents.split("\r\n");

  let fileHeaders = [];
  let lineCount = 0;
  let bidLine = {};
  const positions = await getPermanentBidPositions(contract);

  lines.forEach((line) => {
    const row = line.split(",");
    if (lineCount === 0) {
      for (let i = 0; i < row.length; i++) {
        fileHeaders[i] = row[i].trim();
      }
      lineCount++;
    } else {
      bidLine = csvToJson(fileHeaders, row);
      bidLine.id = lineCount;
      if (!isBidLineIncomplete(bidLine)) {
        if (validateBidLines(bidLine, contract, positions)) {
          result.bidLines.push(translateBidLine(contract, bidLine, positions));
          lineCount++;
        } else {
          throw new Error("Error importing Bid Line data");
        }
      } else {
        console.log("skip loading incomplete bidline");
      }
    }
  });
  return result;
};

export const getPermanentBidPositions = async (contract) => {
  return getPermanentBidPositionsByContract(contract)
    .then((res) => res.json())
    .then((result) => {
      let positions = [];
      result.forEach((pos) => {
        positions.push(...pos.positions);
      });
      return positions;
    });
};

/**
 * This method is to handle the below edge case
 * When the last row(s) are removed from the CSV on a Windows machine, it is adding an extra empty string at the end of CSV.
 * This method checks if BidLineSequence value is empty, if so we skip processing the incomplete row instead of throwing an import error
 * */
export const isBidLineIncomplete = (bidLine) => {
  return bidLine["BidLineSequence"] === "";
};

function getPermanentBidPositionOriginalValue(contract, positions, importedPositionValue) {
  if (contract === NON_CONTRACT)
    /**
     * This is to format the first letter of each word to uppercase and the rest to lowercase
     * Example: "freight wareHouse" -> "Freight Warehouse"
     */
    return importedPositionValue
      .split(" ")
      .map((word) => {
        return word[0].toUpperCase() + word.substring(1).toLowerCase();
      })
      .join(" ");
  else return positions.find((pos) => pos.toUpperCase() === importedPositionValue.toUpperCase());
}

export const translateBidLine = (contract, fileData, positions) => {
  let translatedBidLine = {
    ftpt: fileData["FTorPT"].trim().toUpperCase(),
    id: fileData.id,
    lineNumber: fileData.id,
    shiftDiff: fileData["Premium"].trim(),
    shifts: [
      {
        day: "Sunday",
        ...(fileData["SundayShift"].trim().toUpperCase() === "OFF"
          ? { isOff: true }
          : {
              start: fileData["SundayShift"].trim().split("-")[0],
              end: fileData["SundayShift"].trim().split("-")[1],
              position: getPermanentBidPositionOriginalValue(contract, positions, fileData["SundayPosition"].trim()),
              assignment: fileData["SundayAssignment"].trim(),
            }),
      },
      {
        day: "Monday",
        ...(fileData["MondayShift"].trim().toUpperCase() === "OFF"
          ? { isOff: true }
          : {
              start: fileData["MondayShift"].trim().split("-")[0],
              end: fileData["MondayShift"].trim().split("-")[1],
              position: getPermanentBidPositionOriginalValue(contract, positions, fileData["MondayPosition"].trim()),
              assignment: fileData["MondayAssignment"].trim(),
            }),
      },
      {
        day: "Tuesday",
        ...(fileData["TuesdayShift"].trim().toUpperCase() === "OFF"
          ? { isOff: true }
          : {
              start: fileData["TuesdayShift"].trim().split("-")[0],
              end: fileData["TuesdayShift"].trim().split("-")[1],
              position: getPermanentBidPositionOriginalValue(contract, positions, fileData["TuesdayPosition"].trim()),
              assignment: fileData["TuesdayAssignment"].trim(),
            }),
      },
      {
        day: "Wednesday",
        ...(fileData["WednesdayShift"].trim().toUpperCase() === "OFF"
          ? { isOff: true }
          : {
              start: fileData["WednesdayShift"].trim().split("-")[0],
              end: fileData["WednesdayShift"].trim().split("-")[1],
              position: getPermanentBidPositionOriginalValue(contract, positions, fileData["WednesdayPosition"].trim()),
              assignment: fileData["WednesdayAssignment"].trim(),
            }),
      },
      {
        day: "Thursday",
        ...(fileData["ThursdayShift"].trim().toUpperCase() === "OFF"
          ? { isOff: true }
          : {
              start: fileData["ThursdayShift"].trim().split("-")[0],
              end: fileData["ThursdayShift"].trim().split("-")[1],
              position: getPermanentBidPositionOriginalValue(contract, positions, fileData["ThursdayPosition"].trim()),
              assignment: fileData["ThursdayAssignment"].trim(),
            }),
      },
      {
        day: "Friday",
        ...(fileData["FridayShift"].trim().toUpperCase() === "OFF"
          ? { isOff: true }
          : {
              start: fileData["FridayShift"].trim().split("-")[0],
              end: fileData["FridayShift"].trim().split("-")[1],
              position: getPermanentBidPositionOriginalValue(contract, positions, fileData["FridayPosition"].trim()),
              assignment: fileData["FridayAssignment"].trim(),
            }),
      },
      {
        day: "Saturday",
        ...(fileData["SaturdayShift"].trim().toUpperCase() === "OFF"
          ? { isOff: true }
          : {
              start: fileData["SaturdayShift"].trim().split("-")[0],
              end: fileData["SaturdayShift"].trim().split("-")[1],
              position: getPermanentBidPositionOriginalValue(contract, positions, fileData["SaturdayPosition"].trim()),
              assignment: fileData["SaturdayAssignment"].trim(),
            }),
      },
    ],
    isBlockLine: false,
    isExpanded: false,
    assignedEmployee: null,
  };

  return translatedBidLine;
};

export const isSchedDatesEntered = (schedDates) => {
  return schedDates[0] !== null && schedDates[1] !== null;
};

export const isWindowDatesEntered = (windowDates) => {
  return windowDates[0] !== null && windowDates[1] !== null;
};

export const isBidLineAndBidGroupTotalsMatching = (bidLines, bidGroup) => {
  return bidLines.length === bidGroup.length;
};

function isEmployeeEligibleForShiftBidChoices(employeeRow, initialBidGroup, bidStatus, userSessionEid) {
  if (
    employeeRow.isRemoved ||
    employeeRow.eid === userSessionEid ||
    isPendingBid(bidStatus) ||
    isDeletedBid(bidStatus) ||
    isAwardedBid(bidStatus)
  )
    return false;
  else {
    let idx = initialBidGroup.findIndex((emp) => emp.eid === employeeRow.eid);
    if (idx === -1) return false;
  }
  return true;
}

export const getBidGroupColumns = (
  { bidGroup, contract, seniorityListType, workgroup, bidStatus, rankStyle, initialBidGroup, userSessionEid },
  handleIneligibleClick,
  handleRemoveEmployee,
  handleAddRemovedEmployee,
  handleEmployeeChoicesAsAdmin,
  setSelectedEmployeeForAudit
) => {
  let columns = [];
  let baseColumns = [
    {
      field: "ineligible",
      headerName: "Ineligible",
      width: 80,
      type: "boolean",
      sortable: false,
      renderCell: (params) => {
        return (
          <Checkbox
            checked={!params.row.isEligible}
            onChange={() => {
              handleIneligibleClick(params.row.eid);
            }}
            disabled={
              !!params.row.isRemoved || params.row.rank === "-" || isDeletedBid(bidStatus) || isAwardedBid(bidStatus)
            }
          />
        );
      },
    },
    {
      field: "rank",
      headerName: "Rank",
      minWidth: 70,
      type: "string",
      sortingOrder: ["asc", "desc"],
      renderCell: (params) => {
        return (
          <TextField
            variant="standard"
            InputProps={{
              className: rankStyle,
            }}
            size="small"
            value={params.row.rank ? params.row.rank : ""}
            required
            error={params.row.rank === ""}
            disabled={!!params.row.isRemoved || isAwardedBid(bidStatus)}
          />
        );
      },
      flex: 0.12,
      sortComparator: sortSeniorityList,
      editable: !(isAwardedBid(bidStatus) || isDeletedBid(bidStatus)),
    },
    {
      field: "eid",
      headerName: "EID",
      type: "string",
      sortable: false,
      minWidth: 100,
      flex: 0.15,
    },
    {
      field: "name",
      headerName: "Name",
      type: "string",
      minWidth: 250,
      sortable: false,
      renderCell: (params) => {
        return (
          <>
            {isEmployeeEligibleForShiftBidChoices(params.row, initialBidGroup, bidStatus, userSessionEid) ? (
              <Typography
                style={{ cursor: "pointer" }}
                fontSize="14px"
                onClick={() => {
                  handleEmployeeChoicesAsAdmin(params.row);
                }}
              >
                <u>
                  {params.row.lastName}, {params.row.shortName}
                </u>
              </Typography>
            ) : (
              <>
                {params.row.lastName}, {params.row.shortName}
              </>
            )}
          </>
        );
      },
      flex: 0.3,
    },
    {
      field: "phoneNumber",
      headerName: "Phone #",
      type: "string",
      minWidth: 120,
      sortable: false,
      renderCell: (params) => {
        if (!params.row.phoneNumber) return "-";
        else return params.row.phoneNumber;
      },
      flex: 0.15,
    },
    {
      field: "ftpt",
      headerName: "FT/PT",
      type: "string",
      minWidth: 80,
      sortable: false,
      renderCell: (params) => {
        if (params.row.ftpt === "" || params.row.ftpt === SENIORITY_MISSING_DATA) return "-";
        return params.row.ftpt;
      },
      flex: 0.15,
    },
  ];

  let contractBasedColumns = [];
  if (contract === IAM_CONTRACT) {
    contractBasedColumns.push(
      ...[
        {
          field: "classificationDate",
          headerName: "Classification Date",
          type: "dateTime",
          minWidth: 170,
          sortable: false,
          renderCell: (params) => {
            if (params.row.classificationDate === "" || params.row.classificationDate === SENIORITY_MISSING_DATA)
              return "-";
            return params.row.classificationDate;
          },
          flex: 0.25,
        },
        {
          field: "contractDate",
          headerName: "Contract Date",
          type: "dateTime",
          minWidth: 170,
          sortable: false,
          renderCell: (params) => {
            if (params.row.contractDate === "" || params.row.contractDate === SENIORITY_MISSING_DATA) return "-";
            return params.row.contractDate;
          },
          flex: 0.25,
        },
        {
          field: "hireDate",
          headerName: "Hire Date",
          type: "dateTime",
          minWidth: 120,
          sortable: false,
          renderCell: (params) => {
            if (params.row.hireDate === "" || params.row.hireDate === SENIORITY_MISSING_DATA) return "-";
            return params.row.hireDate;
          },
          flex: 0.25,
        },
        {
          field: "birthDate",
          headerName: "Birth Date",
          type: "dateTime",
          minWidth: 120,
          sortable: false,
          renderCell: (params) => {
            if (params.row.birthDate === "" || params.row.birthDate === SENIORITY_MISSING_DATA) return "-";
            return params.row.birthDate;
          },
          flex: 0.25,
        },
      ]
    );
  } else if (contract === TWU_CONTRACT) {
    contractBasedColumns.push(
      ...[
        {
          field: "wnSliSeniorityDate",
          headerName: "WN SLI Seniority Date",
          type: "dateTime",
          minWidth: 170,
          sortable: false,
          renderCell: (params) => {
            if (params.row.wnSliSeniorityDate === "" || params.row.wnSliSeniorityDate === SENIORITY_MISSING_DATA)
              return "-";
            return params.row.wnSliSeniorityDate;
          },
          flex: 0.25,
        },
        {
          field: "flAtlSeniorityDate",
          headerName: "FL ATL Seniority Date",
          type: "dateTime",
          sortable: false,
          minWidth: 170,
          renderCell: (params) => {
            if (params.row.flAtlSeniorityDate === "" || params.row.flAtlSeniorityDate === SENIORITY_MISSING_DATA)
              return "-";
            return params.row.flAtlSeniorityDate;
          },
          flex: 0.25,
        },
        {
          field: "dualSeniorityDate",
          headerName: "Dual Seniority Date",
          type: "dateTime",
          minWidth: 170,
          sortable: false,
          renderCell: (params) => {
            if (params.row.dualSeniorityDate === "" || params.row.dualSeniorityDate === SENIORITY_MISSING_DATA)
              return "-";
            return params.row.dualSeniorityDate;
          },
          flex: 0.25,
        },
        {
          field: "hireDate",
          headerName: "Hire Date",
          type: "dateTime",
          minWidth: 120,
          sortable: false,
          renderCell: (params) => {
            if (params.row.hireDate === "" || params.row.hireDate === SENIORITY_MISSING_DATA) return "-";
            return params.row.hireDate;
          },
          flex: 0.15,
        },
        {
          field: "birthDate",
          headerName: "Birth Date",
          type: "dateTime",
          minWidth: 120,
          sortable: false,
          renderCell: (params) => {
            if (params.row.birthDate === "" || params.row.birthDate === SENIORITY_MISSING_DATA) return "-";
            return params.row.birthDate;
          },
          flex: 0.15,
        },
      ]
    );
  } else if (contract === NON_CONTRACT) {
    contractBasedColumns.push(
      ...[
        {
          field: "seniorityDate",
          headerName: "Seniority Date",
          type: "dateTime",
          minWidth: 170,
          sortable: false,
          renderCell: (params) => {
            if (params.row.seniorityDate === "" || params.row.seniorityDate === SENIORITY_MISSING_DATA) return "-";
            return params.row.seniorityDate;
          },
          flex: 0.25,
        },
        {
          field: "hireDate",
          headerName: "Hire Date",
          type: "dateTime",
          minWidth: 120,
          sortable: false,
          renderCell: (params) => {
            if (params.row.hireDate === "" || params.row.hireDate === SENIORITY_MISSING_DATA) return "-";
            return params.row.hireDate;
          },
          flex: 0.25,
        },
        {
          field: "birthDate",
          headerName: "Birth Date",
          type: "dateTime",
          minWidth: 120,
          sortable: false,
          renderCell: (params) => {
            if (params.row.birthDate === "" || params.row.birthDate === SENIORITY_MISSING_DATA) return "-";
            return params.row.birthDate;
          },
          flex: 0.25,
        },
      ]
    );
  } else {
    console.log("error unrecognized contract: ", contract);
  }

  let workgroupBasedColumns = [];

  // TODO : set width
  if (workgroup === WORKGROUP_MANAGER) {
    workgroupBasedColumns.push({
      field: "jobTitle",
      headerName: "Job Title",
      type: "string",
      sortable: false,
      flex: 0.3,
    });
  }

  const staticRightColumns = [
    {
      headerName: "",
      field: "comments",
      type: "string",
      width: 25,
      sortable: false,
      renderCell: (params) => {
        if (params?.row?.comments) {
          return <CommentIcon sx={{ cursor: "pointer" }} onClick={() => setSelectedEmployeeForAudit(params?.row)} />;
        }
      },
    },
    {
      field: "removeOrAddEmployee",
      headerName: "",
      width: 20,
      renderCell: (params) => {
        if (!params?.row?.isRemoved) {
          return (
            <IconButton
              disabled={isDeletedBid(bidStatus) || isAwardedBid(bidStatus)}
              sx={{ cursor: "pointer" }}
              onClick={() => handleRemoveEmployee(params.row)}
            >
              <RemoveCircleOutline fontSize="small" />
            </IconButton>
          );
        }

        return (
          <Tooltip
            title={
              getEmployeeUnrankedCount(getEligibleEmployees(bidGroup)) > 0 && BID_GROUP_ADD_EMPLOYEE_WARNING_MESSAGE
            }
            placement="bottom"
            arrow
          >
            <div>
              <IconButton
                disabled={
                  isDeletedBid(bidStatus) ||
                  getEmployeeUnrankedCount(getEligibleEmployees(bidGroup)) > 0 ||
                  isAwardedBid(bidStatus)
                }
                sx={{ color: "secondary.swaGray", cursor: "pointer" }}
                onClick={() => handleAddRemovedEmployee(params.row)}
              >
                <AddCircle fontSize="small" />
              </IconButton>
            </div>
          </Tooltip>
        );
      },
    },
  ];

  columns.push(...baseColumns);
  columns.push(...workgroupBasedColumns);
  columns.push(...contractBasedColumns);
  columns.push(...staticRightColumns);

  return columns;
};

function CustomDetailPanelToggle(props) {
  const { id, value: isExpanded } = props;
  const apiRef = useGridApiContext();

  // To avoid calling ´getDetailPanelContent` all the time, the following selector
  // gives an object with the detail panel content for each row id.
  const contentCache = useGridSelector(apiRef, gridDetailPanelExpandedRowsContentCacheSelector);

  // If the value is not a valid React element, it means that the row has no detail panel.
  const hasDetail = React.isValidElement(contentCache[id]);

  return (
    <IconButton size="small" tabIndex={-1} disabled={!hasDetail} aria-label={isExpanded ? "Close" : "Open"}>
      <ExpandMoreIcon
        sx={{
          transform: `rotateZ(${isExpanded ? 180 : 0}deg)`,
          transition: (theme) =>
            theme.transitions.create("transform", {
              duration: theme.transitions.duration.shortest,
            }),
        }}
        fontSize="inherit"
      />
    </IconButton>
  );
}

export const getBidLineColumns = ({ contract }, handleBlockLineClick, bidStatus) => {
  let columns = [];
  let baseColumns = [
    {
      field: "lineNumber",
      headerName: "Bid Line",
      sortable: false,
      width: 75,
    },
    {
      field: "ftpt",
      headerName: "FT/PT",
      sortable: false,
      width: 65,
    },
    {
      field: "Sunday",
      headerName: "Sun",
      sortable: false,
      minWidth: 120,
      cellClassName: (params) => {
        const shift = params.row.shifts.find((shift) => shift.day === "Sunday");
        if (shift.isOff) {
          return "gray-color-cell";
        }
      },
      renderCell: (params) => {
        const shift = params.row.shifts.find((shift) => shift.day === "Sunday");
        return <BidLineDayCell shift={shift} />;
      },
      flex: 0.15,
    },
    {
      field: "Monday",
      headerName: "Mon",
      sortable: false,
      minWidth: 120,
      cellClassName: (params) => {
        const shift = params.row.shifts.find((shift) => shift.day === "Monday");
        if (shift.isOff) {
          return "gray-color-cell";
        }
      },
      renderCell: (params) => {
        const shift = params.row.shifts.find((shift) => shift.day === "Monday");
        return <BidLineDayCell shift={shift} />;
      },
      flex: 0.15,
    },
    {
      field: "Tuesday",
      headerName: "Tue",
      sortable: false,
      minWidth: 120,
      cellClassName: (params) => {
        const shift = params.row.shifts.find((shift) => shift.day === "Tuesday");
        if (shift.isOff) {
          return "gray-color-cell";
        }
      },
      renderCell: (params) => {
        const shift = params.row.shifts.find((shift) => shift.day === "Tuesday");
        return <BidLineDayCell shift={shift} />;
      },
      flex: 0.15,
    },
    {
      field: "Wednesday",
      headerName: "Wed",
      sortable: false,
      minWidth: 120,
      cellClassName: (params) => {
        const shift = params.row.shifts.find((shift) => shift.day === "Wednesday");
        if (shift.isOff) {
          return "gray-color-cell";
        }
      },
      renderCell: (params) => {
        const shift = params.row.shifts.find((shift) => shift.day === "Wednesday");
        return <BidLineDayCell shift={shift} />;
      },
      flex: 0.15,
    },
    {
      field: "Thursday",
      headerName: "Thu",
      sortable: false,
      minWidth: 120,
      cellClassName: (params) => {
        const shift = params.row.shifts.find((shift) => shift.day === "Thursday");
        if (shift.isOff) {
          return "gray-color-cell";
        }
      },
      renderCell: (params) => {
        const shift = params.row.shifts.find((shift) => shift.day === "Thursday");
        return <BidLineDayCell shift={shift} />;
      },
      flex: 0.15,
    },
    {
      field: "Friday",
      headerName: "Fri",
      sortable: false,
      minWidth: 120,
      cellClassName: (params) => {
        const shift = params.row.shifts.find((shift) => shift.day === "Friday");
        if (shift.isOff) {
          return "gray-color-cell";
        }
      },
      renderCell: (params) => {
        const shift = params.row.shifts.find((shift) => shift.day === "Friday");
        return <BidLineDayCell shift={shift} />;
      },
      flex: 0.15,
    },
    {
      field: "Saturday",
      headerName: "Sat",
      sortable: false,
      minWidth: 120,
      cellClassName: (params) => {
        const shift = params.row.shifts.find((shift) => shift.day === "Saturday");
        if (shift.isOff) {
          return "gray-color-cell";
        }
      },
      renderCell: (params) => {
        const shift = params.row.shifts.find((shift) => shift.day === "Saturday");
        return <BidLineDayCell shift={shift} />;
      },
      flex: 0.15,
    },
    {
      field: "shiftDiff",
      headerName: "Shift Diff",
      sortable: false,
      width: 75,
    },
  ];

  let contractBasedColumns = [];
  if (contract === NON_CONTRACT) {
    contractBasedColumns.push(
      ...[
        {
          ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
          renderCell: (params) =>
            params.row.isBlockLine ? <CustomDetailPanelToggle id={params.id} value={params.value} /> : "",
          width: 40,
          minWidth: 25,
        },
        {
          field: "blockLine",
          headerName: "Block Line",
          width: 87,
          sortable: false,
          type: "boolean",
          renderCell: (params) => {
            return (
              <Checkbox
                checked={!!params.row?.isBlockLine}
                onChange={() => {
                  handleBlockLineClick(params.row.id);
                }}
                disabled={isDeletedBid(bidStatus) || isAwardedBid(bidStatus) || params.row.isDupedLine}
              />
            );
          },
        },
      ]
    );
  }

  columns.push(...baseColumns);
  columns.push(...contractBasedColumns);
  return columns;
};

export const calculateTimeLeft = (targetDateTime, station) => {
  const currentDateTime = DateTime.now().setZone(getTimeZoneFromSwaStation(station));
  const diff = targetDateTime.diff(currentDateTime);
  if (diff.as("milliseconds") > 0) {
    return {
      days: Math.floor(diff.as("days")),
      hours: Math.floor(diff.as("hours") % 24),
      minutes: Math.floor(diff.as("minutes") % 60),
      seconds: Math.floor(diff.as("seconds") % 60),
    };
  } else {
    return null; // Timer expired
  }
};

export const getBlockLinesCount = (bidLines) => {
  return bidLines?.filter((bidLine) => bidLine.isBlockLine === true).length;
};

export const getEmployeeErrorsCount = (bidLines) => {
  return bidLines?.filter((bidLine) => bidLine.isError === true).length;
};

export const getEmployeeInBidGroup = (eid, bidGroup) => {
  const employee = bidGroup.filter((emp) => emp.eid === eid);
  if (employee.length > 0) {
    return employee[0];
  }
  return null;
};

export const getReferenceNumberFromHref = () => {
  const uriList = window.location.href.split("/");
  return uriList[uriList.length - 2];
};

export const getEidFromHref = () => {
  const uriList = window.location.href.split("/");
  return uriList[uriList.length - 1];
};

export const getAwardTabBidLinesColumns = (
  handleAwardedNameBidChoicesClick,
  handleAwardedNamePermanentBidClick,
  bidContractType
) => {
  let columns = [
    {
      field: "lineNumber",
      headerName: "Bid Line",
      sortable: false,
      width: 45,
      renderCell: (params) => {
        return params.row.dupedLineNumber ? params.row.dupedLineNumber : params.row.lineNumber;
      },
    },
    {
      field: "awardType",
      headerName: "Award",
      sortable: false,
      width: 90,
    },
    {
      field: "awardedRank",
      headerName: "Rank",
      sortable: false,
      width: 60,
    },
    {
      field: "awardedEid",
      headerName: "EID",
      sortable: false,
      width: 90,
    },
    {
      field: "awardedName",
      headerName: "Name",
      sortable: false,
      width: 150,
      renderCell: (params) =>
        params.row.awardedEid ? (
          <BidAwardNameCell
            row={params.row}
            handleAwardedNameBidChoicesClick={handleAwardedNameBidChoicesClick}
            handleAwardedNamePermanentBidClick={handleAwardedNamePermanentBidClick}
            bidContractType={bidContractType}
          />
        ) : (
          ""
        ),
    },
    {
      field: "ftpt",
      headerName: "FT/PT",
      sortable: false,
      width: 60,
    },
    {
      field: "Sunday",
      headerName: "Sun",
      sortable: false,
      minWidth: 120,
      cellClassName: (params) => {
        const shift = params.row.shifts.find((shift) => shift.day === "Sunday");
        if (shift.isOff) {
          return "gray-color-cell";
        }
      },
      renderCell: (params) => {
        const shift = params.row.shifts.find((shift) => shift.day === "Sunday");
        return <BidLineDayCell shift={shift} />;
      },
      flex: 0.15,
    },
    {
      field: "Monday",
      headerName: "Mon",
      sortable: false,
      minWidth: 120,
      cellClassName: (params) => {
        const shift = params.row.shifts.find((shift) => shift.day === "Monday");
        if (shift.isOff) {
          return "gray-color-cell";
        }
      },
      renderCell: (params) => {
        const shift = params.row.shifts.find((shift) => shift.day === "Monday");
        return <BidLineDayCell shift={shift} />;
      },
      flex: 0.15,
    },
    {
      field: "Tuesday",
      headerName: "Tue",
      sortable: false,
      minWidth: 120,
      cellClassName: (params) => {
        const shift = params.row.shifts.find((shift) => shift.day === "Tuesday");
        if (shift.isOff) {
          return "gray-color-cell";
        }
      },
      renderCell: (params) => {
        const shift = params.row.shifts.find((shift) => shift.day === "Tuesday");
        return <BidLineDayCell shift={shift} />;
      },
      flex: 0.15,
    },
    {
      field: "Wednesday",
      headerName: "Wed",
      sortable: false,
      minWidth: 120,
      cellClassName: (params) => {
        const shift = params.row.shifts.find((shift) => shift.day === "Wednesday");
        if (shift.isOff) {
          return "gray-color-cell";
        }
      },
      renderCell: (params) => {
        const shift = params.row.shifts.find((shift) => shift.day === "Wednesday");
        return <BidLineDayCell shift={shift} />;
      },
      flex: 0.15,
    },
    {
      field: "Thursday",
      headerName: "Thu",
      sortable: false,
      minWidth: 120,
      cellClassName: (params) => {
        const shift = params.row.shifts.find((shift) => shift.day === "Thursday");
        if (shift.isOff) {
          return "gray-color-cell";
        }
      },
      renderCell: (params) => {
        const shift = params.row.shifts.find((shift) => shift.day === "Thursday");
        return <BidLineDayCell shift={shift} />;
      },
      flex: 0.15,
    },
    {
      field: "Friday",
      headerName: "Fri",
      sortable: false,
      minWidth: 120,
      cellClassName: (params) => {
        const shift = params.row.shifts.find((shift) => shift.day === "Friday");
        if (shift.isOff) {
          return "gray-color-cell";
        }
      },
      renderCell: (params) => {
        const shift = params.row.shifts.find((shift) => shift.day === "Friday");
        return <BidLineDayCell shift={shift} />;
      },
      flex: 0.15,
    },
    {
      field: "Saturday",
      headerName: "Sat",
      sortable: false,
      minWidth: 120,
      cellClassName: (params) => {
        const shift = params.row.shifts.find((shift) => shift.day === "Saturday");
        if (shift.isOff) {
          return "gray-color-cell";
        }
      },
      renderCell: (params) => {
        const shift = params.row.shifts.find((shift) => shift.day === "Saturday");
        return <BidLineDayCell shift={shift} />;
      },
      flex: 0.15,
    },
    {
      field: "shiftDiff",
      headerName: "Shift Diff",
      sortable: false,
      width: 75,
    },
  ];

  return columns;
};

export const getAwardTabNoBidEmpColumns = (handleChevronClicked, bidStatus) => {
  let columns = [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      renderCell: (params) => {
        if (
          !isAwardedBid(bidStatus) &&
          (!params.row.awardType?.startsWith("IAO") || params.row.isManualIAOEntryRequired)
        ) {
          // IAO Employees can only be manually assigned
          // after the dupe line award has been ran and it
          // could not find a line for them.
          return (
            <IconButton
              size="small"
              tabIndex={-1}
              aria-label={params.row.isExpanded ? "Close" : "Open"}
              onClick={() => handleChevronClicked(params)}
            >
              <ExpandMoreIcon
                sx={{
                  transform: `rotateZ(${params.row.isExpanded ? 180 : 0}deg)`,
                  transition: (theme) =>
                    theme.transitions.create("transform", {
                      duration: theme.transitions.duration.shortest,
                    }),
                }}
                fontSize="inherit"
              />
            </IconButton>
          );
        }
        return "";
      },
      width: 40,
      minWidth: 25,
    },
    {
      field: "awardType",
      headerName: "Bid",
      sortable: false,
      flex: 0.5,
    },
    {
      field: "rank",
      headerName: "Rank",
      sortable: false,
      flex: 0.4,
    },
    {
      field: "eid",
      headerName: "EID",
      sortable: false,
      flex: 0.65,
    },
    {
      field: "name",
      headerName: "Name",
      sortable: false,
      flex: 1,
      renderCell: (params) => {
        return `${params.row.lastName}, ${params.row.shortName}`;
      },
    },
    {
      field: "ftpt",
      headerName: "FT/PT",
      sortable: false,
      flex: 0.4,
    },
    {
      field: "bidLineConfirm",
      headerName: "",
      sortable: false,
      flex: 0.4,
      renderCell: (params) => {
        return (
          params.row.awardedLine && (
            <Stack direction="column">
              <CheckCircleIcon
                sx={{
                  color: params.row.awardType?.startsWith("IAO") ? "secondary.swaTurquoise" : "primary.swaYellow",
                }}
              />
              <Typography variant="body1">{params.row.awardedLine}</Typography>
            </Stack>
          )
        );
      },
    },
    {
      field: "phoneNumber",
      headerName: "Phone #",
      sortable: false,
      flex: 0.75,
      renderCell: (params) => {
        return params.row.phoneNumber ? params.row.phoneNumber : "-";
      },
    },
  ];

  return columns;
};
export const getShiftPositions = (bidLines) => {
  let positions = {};

  for (const bidLine of bidLines) {
    for (const shift of bidLine.shifts) {
      if (!shift.isOff) {
        const key = shift.position;
        positions[key] = shift.position;
      }
    }
  }

  return Object.freeze(positions);
};

export const getShiftAssignments = (bidLines) => {
  let assignments = {};

  for (const bidLine of bidLines) {
    for (const shift of bidLine.shifts) {
      if (!shift.isOff) {
        const key = shift.assignment;
        assignments[key] = shift.assignment;
      }
    }
  }

  return Object.freeze(assignments);
};

export const getShiftDaysOff = (bidLines) => {
  let daysOff = {};

  for (const bidLine of bidLines) {
    let days = [];
    for (const shift of bidLine.shifts) {
      if (shift.isOff) {
        days.push(shift.day);
      }
    }
    // Sort the days according to the WEEKDAYS_SHORT order
    days.sort((a, b) => WEEKDAYS_SHORT.indexOf(a) - WEEKDAYS_SHORT.indexOf(b));

    // Iterate through the days array and create all valid consecutive day pairs
    for (let i = 0; i < days.length; i++) {
      let currentDay = days[i];
      let nextDay = days[(i + 1) % days.length];

      const key = formatDaysOffKey(currentDay, nextDay);
      if (ALL_BID_DAYS_OFF_FILTERS.hasOwnProperty(key)) {
        daysOff[key] = ALL_BID_DAYS_OFF_FILTERS[key];
      }
    }
  }

  return Object.freeze(daysOff);
};

export const getShiftStartTimes = (bidLines) => {
  let startTimes = {};

  for (const bidLine of bidLines) {
    for (const shift of bidLine.shifts) {
      if (!shift.isOff) {
        const key = shift.start.replace(":", "");
        startTimes[key] = shift.start;
      }
    }
  }

  return Object.freeze(startTimes);
};

export const getNumberOfMyChoicesLabel = (bidLines, selectedChoices) => {
  let numOfMyChoices = 0;
  for (const bidLine of bidLines) {
    if (selectedChoices.includes(bidLine.lineNumber)) {
      numOfMyChoices++;
    }
  }
  return numOfMyChoices;
};

export const hasUnawardedEmployees = (employees) => {
  return employees.filter((emp) => !emp.isRemoved && emp.isEligible && !emp.awardedLine).length > 0;
};

export const isAwardedBidPastTheEndDate = (bidData, schedEndDate) => {
  return isAwardedBid(bidData.status) && isDateTimeInPast(schedEndDate, bidData.station);
};

export const getUnAssignedBlockLinesCount = (bidLines) => {
  return bidLines.filter((bidLine) => bidLine.isBlockLine && bidLine?.assignedEmployee === null).length;
};

export const getBidName = (bidData) => {
  let bidName = "";
  if (bidData) {
    bidName = `${bidData.station} | ${bidData.workgroup} | ${getShiftDate(bidData.schedStartDate)} - ${getShiftDate(
      bidData.schedEndDate
    )}`;
    if (bidData.bidName) {
      bidName += ` | ${bidData.bidName}`;
    }
  }
  return bidName;
};

export const isAssignmentUsedToDisplay = (bidData) => {
  const unionContract = getUnionContractGivenWorkgroup(bidData?.workgroup);

  if (bidData?.stationTier) {
    const stationTier = parseInt(bidData?.stationTier, 10);
    return stationTier >= 2 && stationTier <= 7 && unionContract === TWU_CONTRACT;
  }
  return false;
};

export const getShiftJobType = (shift, useAssignment) => {
  if (useAssignment) {
    return shift.assignment;
  }
  return shift.position;
};
